import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import withIntl from '../../../../components/Intl/WithIntl';
import { Button, ButtonGroup, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { displayErrors, removeError } from '../../ErrorActions';
import { subscribeToAuthFail, isAuthFailError, replayCallsHeldOnAuthFail, releaseCallsHeldOnAuthFail } from '../../../../util/apiCaller';
import { logoutUser, logoutUserRequest } from '../../../User/AuthActions';
import { getCurrentApplicant } from '../../../Applicant/ApplicantActions';
import { getLoggedUser } from '../../../User/UserActions';
import { isOfflineAnswersEnabled, getPendingCount } from '../../../Answer/OfflineAnswerActions';
import UserLoginForm from '../../../User/components/auth/UserLoginForm';
import { notifyError, notifyMessage } from '../../../../components/Error/ErrorBoundary';

function ErrorAlerts({ dispatch, intl, errors, currentApplicant = null, loggedUser = null }) {
    // An expired session is reported by the API caller, and can also reach the error store when a
    // caller forwards the rejection. Both latch the same flag rather than being read in place,
    // because the errors below are drained from the store as soon as they are displayed.
    const [hasAuthFailed, setHasAuthFailed] = useState(false);

    // Heard straight from the API caller, because most callers only log the rejection instead of
    // dispatching it: waiting for the error store alone would miss almost all of them. A refresh
    // that goes through reports no error, which lowers the flag: a login that never refreshed a
    // token reports nothing, and is caught by the logged user read below instead.
    useEffect(() => subscribeToAuthFail(error => setHasAuthFailed(!!error)), []);

    /*
     * A user who arrives settles the expired session, whichever form he used. The modal clears the
     * flag itself, but the login page displayed instead of it stores a token without ever
     * refreshing one, so the API caller reports no recovery and the flag would stay raised: the
     * modal then opened over the page the user had just reached.
     *
     * Only the arrival clears it, never the mere presence of a user: a session expiring under a
     * user still held in the store has to keep raising the modal.
     */
    const previousLoggedUserId = useRef(loggedUser?._id);
    useEffect(() => {
        const hasJustLoggedIn = loggedUser?._id && loggedUser._id !== previousLoggedUserId.current;
        previousLoggedUserId.current = loggedUser?._id;
        hasJustLoggedIn && setHasAuthFailed(false);
    }, [loggedUser?._id]);

    // During a service outage the token refresh fails like everything else. Asking an applicant to
    // log in again, over his exam, while his answers are safely queued on his device would block
    // him for nothing: the modal only shows when the queue is empty, which means the service does
    // answer and the session really did expire.
    const isHoldingAnswers = currentApplicant && isOfflineAnswersEnabled();
    const [hasPendingAnswers, setHasPendingAnswers] = useState(false);
    useEffect(() => {
        if(!isHoldingAnswers || !hasAuthFailed) {
            return undefined;
        }
        let isSubscribed = true;
        getPendingCount(currentApplicant._id).then(pendingCount => {
            isSubscribed && setHasPendingAnswers(!!pendingCount);
        });
        return () => { isSubscribed = false; };
    }, [hasAuthFailed, isHoldingAnswers, currentApplicant?._id]);

    // A login form is already on screen whenever no user is logged: AuthorizationComponent, the
    // /login route and the home page all fall back to UserLoginPage on that same condition. Asking
    // again in a modal would ask twice for the same thing. An applicant is not a logged user
    // though, and never gets that fallback, so his exam keeps raising the modal.
    const isLoginPageDisplayed = !loggedUser && !currentApplicant;

    const isAuthAlertOpen = hasAuthFailed && !hasPendingAnswers && !isLoginPageDisplayed;

    /*
     * Holding a call only makes sense while the modal is there to resolve it. Whenever the session
     * expired but the modal is not shown, nothing would ever settle those calls and their callers
     * would wait on a promise forever, so they are released to fail as they used to. This covers
     * the login page displaying the form instead of the modal, and the applicant whose answers are
     * queued on his device.
     */
    useEffect(() => {
        if(hasAuthFailed && !isAuthAlertOpen) {
            releaseCallsHeldOnAuthFail();
        }
    }, [hasAuthFailed, isAuthAlertOpen]);

    /*
     * Translate an error into the message shown to the user. Every expired session shares the same
     * wording whichever token failed, and an untranslated message falls back to the generic error
     * rather than exposing its technical identifier in the toaster.
     */
    const formatErrorMessage = message => {
        const messageId = isAuthFailError(message) ? 'alertUnauthorized' : `alert${message}`;
        if(!intl.messages[messageId]) {
            console.error('MissingErrorTranslation', messageId, intl);
            return intl.formatMessage({ id: 'alertGenericError', defaultMessage: 'An error occurred! Please try again later...' });
        }
        return intl.formatMessage({ id: messageId });
    };

    // Display or remove errors
    useEffect(() => {
        errors.forEach(error => {
            // console.log('Error to display', error);
            // The store is the only trace of an auth error dispatched by a caller, and it is
            // cleared right below: latch it here or the modal would never see it
            isAuthFailError(error.message) && setHasAuthFailed(true);
            if(error && error.type && error.message) {
                setTimeout(() => { // Wait for component initialization (with intl)
                    displayErrors(error.type, formatErrorMessage(error.message));
                }, 500);
            }
            dispatch(removeError(error.message));
        });
    }, [errors, intl]);

    /*
     * Logging back in resumes what the expired session had interrupted: the calls held meanwhile
     * are replayed with the new token, so the user finds the page he was waiting for instead of an
     * empty screen he has to trigger again himself.
     */
    const handleLogin = () => {
        setHasAuthFailed(false);
        const replayedCount = replayCallsHeldOnAuthFail();
        replayedCount && console.log('Error::: Replay calls held on auth failure', replayedCount);
    };

    /*
     * Dismissing the modal without logging in gives up on those calls: they are rejected as they
     * would have been without the hold, otherwise their callers would wait forever on a promise
     * that never settles, loader included.
     */
    const closeLoginAlert = () => {
        setHasAuthFailed(false);
        releaseCallsHeldOnAuthFail();
    };

    const handleRefreshToLogin = () => {
        dispatch(logoutUser(true));
        // document.location.reload();
    };

    const renderAlert = code => {
        switch(code) {
            case 'auth':
                return (
                    <Modal isOpen size="lg" centered className="text-center" toggle={closeLoginAlert}>
                        {/* <ModalHeader className="text-center">
                            <FontAwesomeIcon icon="exclamation-circle" /> <FormattedMessage id="userAuthAlertTitle" defaultMessage="Authentication" />
                        </ModalHeader> */}
                        <ModalBody>
                            <UserLoginForm onLogin={handleLogin} />
                            {/* <p>
                                <FormattedMessage id="userAuthAlertMessage" defaultMessage="You are not logged anymore! Please login to your account." /><br />
                            </p>
                            {currentApplicant && (
                                <p className="text-warning">
                                    <FormattedMessage id="applicantAnswersSaveOnAuthError" defaultMessage="Please save your answers on another support before clicking on Login" /><br />
                                </p>
                            )}
                            <ButtonGroup>
                                <Button color="primary" outline onClick={() => setAlertCode(null)}><FormattedMessage id="close" defaultMessage="Close" /></Button>
                                <Button outline onClick={handleRefreshToLogin}><FontAwesomeIcon icon="sign-in-alt" /> <FormattedMessage id="userAccountLogin" defaultMessage="Login" /></Button>
                            </ButtonGroup> */}
                        </ModalBody>
                    </Modal>
                );

            default:
        }
    };

    if(isAuthAlertOpen) {
        return renderAlert('auth');
    }
    // return process.env.NODE_ENV === 'development' && <Button onClick={() => notifyError(new Error('TestSendError'), { customData: 123 }) /* notifyMessage('test message', 'warning') */}>Send error</Button>;
    return null;
}

ErrorAlerts.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    errors: PropTypes.arrayOf(PropTypes.object),
    currentApplicant: PropTypes.object,
    loggedUser: PropTypes.object,
};

const mapStateToProps = (store, props) => ({
    errors: store.error.data,
    currentApplicant: getCurrentApplicant(store),
    loggedUser: getLoggedUser(store),
});

export default connect(mapStateToProps)(withIntl(ErrorAlerts));
